import type {
  DraftPolicy,
  DraftPolicySection,
  DraftPolicySubSection,
} from 'views/App/Portal/Policy/_types/manuals/draft';

/** Normalize section/subsection title for matching (trim, collapse whitespace). */
function normalizeTitle(title: string | undefined): string {
  if (title == null) return '';
  return String(title).replace(/\s+/g, ' ').trim();
}

/** Minimal shape of an imported section (e.g. from another manual/env). IDs are ignored. */
export interface ImportSection {
  title?: string;
  content?: string;
  contentEditable?: string;
  subSections?: ImportSubSection[];
  state?: 'ENABLED' | 'DISABLED';
  editLevelTag?: DraftPolicySection['editLevelTag'];
}

/** Minimal shape of an imported subsection. */
export interface ImportSubSection {
  title?: string;
  content?: string;
  contentEditable?: string;
  state?: 'ENABLED' | 'DISABLED';
  editLevelTag?: DraftPolicySubSection['editLevelTag'];
}

/** Parsed import payload: at least sections array. */
export interface ImportPolicyPayload {
  sections?: ImportSection[];
  title?: string;
}

const DEFAULT_EDIT_TAG: DraftPolicySection['editLevelTag'] = 'AGENCY_CONTENT';

function defaultEditTagForLevel(manualLevel: string): DraftPolicySection['editLevelTag'] {
  if (manualLevel === 'GLOBAL') return 'FEDERAL';
  if (manualLevel === 'JURISDICTION') return 'STATE';
  return DEFAULT_EDIT_TAG;
}

function mergeSubSections(
  existingSubs: DraftPolicySubSection[],
  importedSubs: ImportSubSection[] | undefined,
  parentSectionId: string,
  manualLevel: string
): DraftPolicySubSection[] {
  const tag = defaultEditTagForLevel(manualLevel);
  if (!importedSubs?.length) return existingSubs;

  const result: DraftPolicySubSection[] = [];
  const usedExistingIds = new Set<string>();

  for (let i = 0; i < importedSubs.length; i++) {
    const imp = importedSubs[i];
    const impTitle = normalizeTitle(imp.title);
    const existing = existingSubs.find(
      (s) => normalizeTitle(s.title) === impTitle && !usedExistingIds.has(s.id)
    );

    if (existing) {
      usedExistingIds.add(existing.id);
      result.push({
        ...existing,
        title: imp.title ?? existing.title,
        content: imp.content ?? existing.content,
        contentEditable: imp.contentEditable ?? imp.content ?? existing.contentEditable,
      });
    } else {
      const newId = `new_${Date.now()}_s${i}`;
      result.push({
        attachments: [],
        agencyModified: false,
        content: imp.content ?? '',
        contentEditable: imp.contentEditable ?? imp.content ?? '',
        id: newId,
        state: (imp.state as 'ENABLED' | 'DISABLED') ?? 'ENABLED',
        title: imp.title ?? 'Section Title',
        editLevelTag: imp.editLevelTag ?? tag,
        parentId: parentSectionId,
      });
    }
  }

  return result;
}

/**
 * Merges imported policy JSON into the current policy. Reuses existing sections/subsections
 * when titles match (by normalized title); does not use IDs from the imported data.
 * New sections/subsections get temporary IDs (new_*) and will receive server IDs on save.
 */
export function importPolicyFromJson(
  currentPolicy: DraftPolicy,
  imported: ImportPolicyPayload
): DraftPolicy {
  const manualLevel = currentPolicy.manualLevel ?? 'AGENCY';
  const tag = defaultEditTagForLevel(manualLevel);
  const importedSections = imported.sections ?? [];

  if (importedSections.length === 0) {
    return currentPolicy;
  }

  const existingSections = currentPolicy.sections ?? [];
  const mergedSections: DraftPolicySection[] = [];
  const usedSectionIds = new Set<string>();

  for (let i = 0; i < importedSections.length; i++) {
    const imp = importedSections[i];
    const impTitle = normalizeTitle(imp.title);
    const existingSection = existingSections.find(
      (s) => normalizeTitle(s.title) === impTitle && !usedSectionIds.has(s.id)
    );

    if (existingSection) {
      usedSectionIds.add(existingSection.id);
      const mergedSubs = mergeSubSections(
        existingSection.subSections ?? [],
        imp.subSections,
        existingSection.id,
        manualLevel
      );
      mergedSections.push({
        ...existingSection,
        title: imp.title ?? existingSection.title,
        content: imp.content ?? existingSection.content,
        contentEditable: imp.contentEditable ?? imp.content ?? existingSection.contentEditable,
        subSections: mergedSubs,
      });
    } else {
      const baseId = Date.now();
      const newSectionId = `new_${baseId}_${i}`;
      const newSubs = (imp.subSections ?? []).map((sub, j): DraftPolicySubSection => ({
        attachments: [],
        agencyModified: false,
        content: sub.content ?? '',
        contentEditable: sub.contentEditable ?? sub.content ?? '',
        id: `new_${baseId}_${i}_${j}`,
        state: (sub.state as 'ENABLED' | 'DISABLED') ?? 'ENABLED',
        title: sub.title ?? 'Section Title',
        editLevelTag: (sub.editLevelTag ?? tag) as DraftPolicySubSection['editLevelTag'],
        parentId: newSectionId,
      }));
      mergedSections.push({
        attachments: [],
        agencyModified: false,
        content: imp.content ?? '',
        contentEditable: imp.contentEditable ?? imp.content ?? '',
        id: newSectionId,
        state: (imp.state as 'ENABLED' | 'DISABLED') ?? 'ENABLED',
        title: imp.title ?? 'Section Title',
        editLevelTag: (imp.editLevelTag ?? tag) as DraftPolicySection['editLevelTag'],
        subSections: newSubs,
        isNew: false,
        parentId: null,
      });
    }
  }

  return {
    ...currentPolicy,
    sections: mergedSections,
    ...(imported.title != null && imported.title !== '' ? { title: imported.title } : {}),
  };
}
